import type {
  Survey,
  CreateSurveyRequest,
  UpdateSurveyRequest,
  SubmitAnswersRequest,
  Answer,
  AIFollowUpQuestion,
  CreateRespondentRequest,
  ApiResponse,
} from '@nordcrest/shared';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const data = await response.json();
  return data as ApiResponse<T>;
}

// Survey endpoints
export async function createSurvey(data: CreateSurveyRequest): Promise<Survey> {
  const response = await fetchApi<Survey>('/surveys', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to create survey');
  }

  return response.data;
}

export async function getSurvey(id: string): Promise<{ survey: Survey; answers: Answer[] }> {
  const response = await fetchApi<{ survey: Survey; answers: Answer[] }>(`/surveys/${id}`);

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Survey not found');
  }

  return response.data;
}

export async function updateSurvey(id: string, data: UpdateSurveyRequest): Promise<Survey> {
  const response = await fetchApi<Survey>(`/surveys/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to update survey');
  }

  return response.data;
}

export async function submitAnswers(surveyId: string, data: SubmitAnswersRequest): Promise<Answer[]> {
  const response = await fetchApi<Answer[]>(`/surveys/${surveyId}/answers`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to submit answers');
  }

  return response.data;
}

export async function getAnswersMap(
  surveyId: string
): Promise<Record<string, string | string[] | number | boolean | null>> {
  const response = await fetchApi<Record<string, string | string[] | number | boolean | null>>(
    `/surveys/${surveyId}/answers-map`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to get answers');
  }

  return response.data;
}

// AI endpoints
export async function getFollowUpQuestions(surveyId: string): Promise<AIFollowUpQuestion[]> {
  const response = await fetchApi<AIFollowUpQuestion[]>(
    `/ai/surveys/${surveyId}/follow-up-questions`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to get follow-up questions');
  }

  return response.data;
}

export async function submitFollowUpAnswer(
  followUpId: string,
  value: string | string[] | number | null
): Promise<void> {
  const response = await fetchApi(`/ai/follow-ups/${followUpId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ value }),
  });

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to submit answer');
  }
}

// Report endpoints
export async function submitSurveyAndGenerateReport(
  surveyId: string,
  data: CreateRespondentRequest
): Promise<{ message: string; respondentId: string }> {
  const response = await fetchApi<{ message: string; respondentId: string }>(
    `/reports/survey/${surveyId}`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to submit survey');
  }

  return response.data;
}
